import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Fuel, Car, DollarSign, Printer, AlertTriangle } from 'lucide-react';
import FuelLogModal from './modals/FuelLogModal';
import VehicleModal from './modals/VehicleModal';
import FuelPricingModal from './modals/FuelPricingModal';
import FuelPricingCalendarModal from './modals/FuelPricingCalendarModal';
import FuelReportModal from './modals/FuelReportModal';
import { Gate } from './AccessStudio';

const Gas = ({ apiCall, hasRole, hasPermission, showSuccess, showError }) => {
  const [vehicles, setVehicles] = useState([]);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelPrices, setFuelPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPricingCalendarModal, setShowPricingCalendarModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Date filters - default to last month until today
  const [dateFrom, setDateFrom] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]
  );
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  // Permission checks (now granular; banners/modals/logic use these,
  // while the action buttons use <Gate> for the in-context overlay)
  const canAddFuelLog = hasPermission ? hasPermission('gas', 'fuel_logs', 'create') : true;
  const canSetPrices = hasPermission ? hasPermission('gas', 'pricing', 'create') : true;
  const canViewReports = hasPermission ? hasPermission('gas', 'reports', 'view') : true;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load vehicles
      const vehiclesData = await apiCall('/gas/vehicles');
      setVehicles(vehiclesData || []);
      
      // Load fuel logs with date filter - use data directly from database
      const logs = await apiCall(`/gas/logs?dateFrom=${dateFrom}&dateTo=${dateTo}`);
      setFuelLogs(logs || []);
      
      // Load current fuel prices (ONLY FOR TODAY with Option 1)
      const prices = await apiCall('/gas/pricing/current');
      setFuelPrices(prices || []);
      
    } catch (error) {
      console.error('Error loading gas data:', error);
      showError('Error loading gas data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, dateFrom, dateTo, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for calendar open event
  useEffect(() => {
    const handleOpenCalendar = () => {
      setShowPricingCalendarModal(true);
    };
    
    window.addEventListener('openPricingCalendar', handleOpenCalendar);
    
    return () => {
      window.removeEventListener('openPricingCalendar', handleOpenCalendar);
    };
  }, []);

  const handleLogSuccess = (message) => {
    setShowLogModal(false);
    loadData();
    showSuccess(message || 'Fuel log added successfully');
  };

  const handleVehicleSuccess = () => {
    setShowVehicleModal(false);
    loadData();
    showSuccess('Vehicle added successfully');
  };

  const handlePricingSuccess = () => {
    setShowPricingModal(false);
    loadData();
    showSuccess('Fuel prices updated successfully');
  };

  const handlePricingCalendarSuccess = () => {
    setShowPricingCalendarModal(false);
    loadData();
    showSuccess('Pricing calendar updated');
  };

  // Calculate statistics for filtered period
  const totalCost = fuelLogs.reduce((sum, log) => sum + (log.Cost || 0), 0);
  const totalVolume = fuelLogs.reduce((sum, log) => sum + (log.Volume || 0), 0);
  const pendingLogsCount = fuelLogs.filter(log => log.Cost === null || log.Cost === undefined).length;

  // Check if prices are set for today
  const pricesSetForToday = fuelPrices.length > 0;

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      {/* Current Fuel Prices Banner - ALWAYS VISIBLE */}
      <div className={`rounded-xl shadow-lg p-4 mb-6 text-white ${
        pricesSetForToday 
          ? 'bg-gradient-to-r from-green-600 to-blue-600' 
          : 'bg-gradient-to-r from-orange-500 to-red-500'
      }`}>
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-2">
              Fuel Prices for {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            {pricesSetForToday ? (
              <div>
                <div className="flex gap-6 flex-wrap">
                  {fuelPrices.map((price, idx) => (
                    <div key={idx}>
                      <span className="font-semibold">{price.FuelType}:</span>{' '}
                      <span className="text-xl font-bold">${price.PricePerLiter.toFixed(3)}/L</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <span className="text-xs text-green-100">
                    ✓ Prices are set for today - All fuel logs will be calculated with these prices
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <AlertTriangle size={32} className="text-yellow-200" />
                <div>
                  <p className="text-lg font-bold text-yellow-100">
                    No fuel prices set for today yet
                  </p>
                  <p className="text-sm text-yellow-50">
                    {canSetPrices 
                      ? 'Set today\'s prices when ready. Logs added today will be calculated once prices are set.' 
                      : 'Fuel logs can still be added. Costs will be calculated once Accounting sets today\'s prices.'}
                  </p>
                  <p className="text-xs text-yellow-200 mt-1">
                    Note: The system will automatically calculate costs when prices are added later
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {/* View Report Button - All authorized users can view */}
            <Gate permission="gas.reports.view" label="View Report">
              <button
                onClick={() => setShowReportModal(true)}
                className="bg-white text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
              >
                <Printer size={18} />
                View Report
              </button>
            </Gate>
            {/* Set Prices Button - Only Admin and Accounting */}
            <Gate permission="gas.pricing.create" label="Set Prices">
              <button
                onClick={() => setShowPricingModal(true)}
                className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transition-all ${
                  pricesSetForToday
                    ? 'bg-white text-green-600 hover:bg-green-50'
                    : 'bg-yellow-400 text-red-800 hover:bg-yellow-300 animate-pulse'
                }`}
              >
                <DollarSign size={18} />
                {pricesSetForToday ? 'Update Today\'s Prices' : 'SET TODAY\'S PRICES'}
              </button>
            </Gate>
          </div>
        </div>
      </div>

      {/* Info message if no prices are set but user can add logs */}
      {!pricesSetForToday && canAddFuelLog && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-blue-900">
                You can still add fuel logs
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Fuel logs can be recorded even without today's prices. The system will automatically calculate costs 
                once Accounting sets the prices for today. This ensures continuous operation of the gas station.
              </p>
              {pendingLogsCount > 0 && (
                <p className="text-sm text-blue-800 mt-2 font-semibold">
                  Currently {pendingLogsCount} log{pendingLogsCount > 1 ? 's are' : ' is'} waiting for price calculation.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reminder for Accounting if there are pending logs */}
      {!pricesSetForToday && canSetPrices && pendingLogsCount > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-yellow-600" size={24} />
            <div>
              <p className="font-semibold text-yellow-800">
                Action Required: {pendingLogsCount} fuel log{pendingLogsCount > 1 ? 's' : ''} pending price calculation
              </p>
              <p className="text-sm text-yellow-700">
                Please set today's fuel prices to calculate costs for pending transactions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Car className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Vehicles</p>
              <p className="text-2xl font-bold text-gray-800">{vehicles.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <Fuel className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Fuel Logs (Filtered)</p>
              <p className="text-2xl font-bold text-gray-800">{fuelLogs.length}</p>
              {pendingLogsCount > 0 && (
                <p className="text-xs text-orange-600 mt-1 font-semibold">
                  {pendingLogsCount} pending price{pendingLogsCount > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <Fuel className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Volume</p>
              <p className="text-2xl font-bold text-gray-800">{totalVolume.toFixed(2)} L</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <DollarSign className="text-yellow-600" size={24} />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Total Cost (Filtered)</p>
              <p className="text-2xl font-bold text-gray-800">${totalCost.toFixed(2)}</p>
              {pendingLogsCount > 0 && (
                <p className="text-xs text-orange-600 mt-1 font-semibold">
                  Excludes {pendingLogsCount} pending
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Fuel Logs */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Fuel Logs</h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <label className="text-gray-600">From:</label>
              <input 
                type="date" 
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border-0 focus:ring-0 text-sm bg-transparent"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="text-gray-600">To:</label>
              <input 
                type="date" 
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border-0 focus:ring-0 text-sm bg-transparent"
              />
            </div>
            {/* Add Fuel Log Button - Only Admin and GasStationManager */}
            <Gate permission="gas.fuel_logs.create" label="Add Fuel Log">
              <button 
                onClick={() => setShowLogModal(true)} 
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors shadow-md"
                title={!pricesSetForToday ? 'Add fuel log (costs will be calculated when prices are set)' : 'Add a new fuel log'}
              >
                <Plus size={18} />
                Add Fuel Log
              </button>
            </Gate>
          </div>
        </div>
        
        {!pricesSetForToday && canAddFuelLog && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">Note:</span> Fuel logs added today will show as "Pending Price" until today's fuel prices are set by Accounting.
            </p>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Vehicle</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Driver</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Odometer</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Fuel Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Volume (L)</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Price/L</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {fuelLogs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No fuel logs found for the selected period.
                  </td>
                </tr>
              ) : (
                fuelLogs.map(log => (
                  <tr key={log.FuelLogID} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      {new Date(log.FuelDate).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800">{log.VehicleNumber}</td>
                    <td className="px-4 py-3 text-gray-700">{log.DriverName || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-600">{log.OdometerReading.toFixed(1)} km</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                        {log.FuelType}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-purple-600">{log.Volume.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      {log.Cost !== null && log.Cost !== undefined && log.Volume > 0 ? (
                        <span className="text-gray-600 font-medium">
                          ${(log.Cost / log.Volume).toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-orange-500 italic text-xs font-semibold">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {log.Cost !== null && log.Cost !== undefined ? (
                        <span className="font-bold text-green-600 text-lg">
                          ${log.Cost.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-orange-500 font-semibold italic text-sm flex items-center gap-1">
                          <DollarSign size={14} />
                          Pending Price
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registered Vehicles */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Registered Vehicles</h3>
          <Gate adminOnly label="Add Vehicle">
            <button 
              onClick={() => setShowVehicleModal(true)} 
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors shadow-md"
            >
              <Plus size={18} />
              Add Vehicle
            </button>
          </Gate>
        </div>
        
        {vehicles.length === 0 ? (
          <div className="text-center py-8">
            <Car className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500 mb-2">No vehicles registered yet.</p>
            <Gate adminOnly label="Add Vehicle hint">
              <p className="text-sm text-gray-400">Click "Add Vehicle" to register your first vehicle.</p>
            </Gate>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {vehicles.map(vehicle => (
              <div 
                key={vehicle.VehicleID} 
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50 hover:bg-white"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Car size={24} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{vehicle.VehicleNumber}</p>
                    <p className="text-sm text-gray-600">{vehicle.VehicleType}</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    vehicle.IsActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {vehicle.IsActive ? '✓ Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals - Note: removed pricesSetForToday condition from showLogModal */}
      {showLogModal && canAddFuelLog && (
        <FuelLogModal
          apiCall={apiCall}
          vehicles={vehicles}
          fuelPrices={fuelPrices}
          onClose={() => setShowLogModal(false)}
          onSuccess={handleLogSuccess}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {showVehicleModal && (
        <VehicleModal
          apiCall={apiCall}
          onClose={() => setShowVehicleModal(false)}
          onSuccess={handleVehicleSuccess}
          showError={showError}
        />
      )}

      {showPricingModal && canSetPrices && (
        <FuelPricingModal
          apiCall={apiCall}
          onClose={() => setShowPricingModal(false)}
          onSuccess={handlePricingSuccess}
          showError={showError}
        />
      )}

      {showPricingCalendarModal && (
        <FuelPricingCalendarModal
          apiCall={apiCall}
          onClose={() => setShowPricingCalendarModal(false)}
          onSuccess={handlePricingCalendarSuccess}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {showReportModal && canViewReports && (
        <FuelReportModal
          apiCall={apiCall}
          vehicles={vehicles}
          onClose={() => setShowReportModal(false)}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}
    </div>
  );
};

export default Gas;